import {
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import Paper from '../paper';
import { HeadCell } from './list';
import ListCell from './list-cell';

const TABLE_PREFIX = 'BodyTable';

const PREFIX = 'ListRow';
const classes = {
  tableRow: `${PREFIX}-tableRow`,
  tableRowExpanded: `${PREFIX}-tableRowExpanded`,
  tableRowNoHover: `${PREFIX}-tableRowNoHover`,
  tableRowHover: `${PREFIX}-tableRowHover`,

  tableHeadCell: `${TABLE_PREFIX}-tableHeadCell`,
  autoWidth: `${TABLE_PREFIX}-autoWidth`,
  fullWidth: `${TABLE_PREFIX}-fullWidth`,
  sticky: `${TABLE_PREFIX}-sticky`,
};

const sharedValues = {
  color: 'inherit',
  display: 'table-row',
  verticalAlign: 'middle',
} as const;

const borderRadius = 10;

type ListRowProps = {
  isMobile?: boolean;
  className?: string | undefined;
  children: React.ReactNode;
  options?: HeadCell[];
  sx?: SxProps<Theme> | undefined;
  onClick?: () => void | undefined;
  body?: { options: HeadCell[]; children: React.ReactNode; span: number; open?: boolean };
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  [`&.${classes.tableRow}`]: {
    ...sharedValues,
    borderRadius: borderRadius, // match td child radius
    // boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.24)',
    backgroundColor: theme.palette.common.white,
    ['& td']: {
      borderBottom: 0,
    },
    '&.MuiTableRow-root td:first-of-type': {
      borderTopLeftRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
      paddingLeft: 16,
    },
    '&.MuiTableRow-root td:last-of-type': {
      borderTopRightRadius: borderRadius,
      borderBottomRightRadius: borderRadius,
      paddingRight: 16,
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '&.MuiTableRow-hover:hover.not(.ListRow-tableRowNoHover)': {
      backgroundColor: theme.palette.common.white,
    },
  },
  [`&.${classes.tableRowExpanded}`]: {
    ...sharedValues,
    borderRadius: 0,
    boxShadow: 'none',
    '&.MuiTableRow-root td:first-of-type': {
      borderTopLeftRadius: borderRadius,
      paddingLeft: 16,
    },
    '&.MuiTableRow-root td:last-of-type': {
      borderTopRightRadius: borderRadius,
      paddingRight: 16,
    },
    backgroundColor: theme.palette.common.white,
    '& td': {
      // borderBottomColor: theme.palette.lightBlue.main,
    },
    '&.MuiTableRow-hover:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  [`&.${classes.tableRowHover}`]: {
    cursor: 'pointer',
  },
  [`&.${classes.tableRowNoHover}`]: {
    cursor: 'default',
  },
}));

const BodyCell = styled(TableCell, { label: 'BodyCell' })(({ theme }) => ({
  borderBottom: 0,
  paddingTop: 0,
  paddingBottom: 0,
  backgroundColor: 'white',
  // boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.24)',
  transition: theme.transitions.create(['padding-top'], {
    duration: theme.transitions.duration.leavingScreen,
  }),
  ['&.expanded']: {
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 20,
    paddingBottom: 20,
  },
}));

const BodyTable = styled(Table)(({ theme }) => ({
  borderCollapse: 'initial',
  [`& .${classes.tableHeadCell}`]: {
    fontSize: 12,
    letterSpacing: 0.7,
    textTransform: 'none',
    padding: theme.spacing(1, 2, 2),
    lineHeight: 1.25,
  },
  [`& .${classes.autoWidth}`]: {
    width: 0,
  },
  [`& .${classes.fullWidth}`]: {
    width: '100%',
  },
  [`& .${classes.sticky}`]: {
    position: 'sticky',
    left: '0',
    background: theme.palette.common.white,
  },
}));

export const BodyTableRow = styled(TableRow, { label: 'BodyTableRow-head' })(({ theme }) => ({
  backgroundColor: 'inherit',
  ['& td']: {
    borderBottom: 0,
  },
  ['& th:first-of-type']: {
    paddingLeft: `8px !important`,
  },
  ['& th:last-of-type']: {
    paddingRight: `8px !important`,
  },
  ['& td:first-of-type']: {
    borderBottomLeftRadius: borderRadius,
  },
  ['& td:last-of-type']: {
    borderBottomRightRadius: borderRadius,
  },
}));

const ListRow: React.FC<ListRowProps> = ({ className, children, options, sx, body, isMobile = false, onClick }) => {
  const theme = useTheme();
  const intl = useIntl();
  // STATE

  const [expanded, setExpanded] = useState<boolean>(body?.open || false);
  const isInternalMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOnClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const target = event.target as HTMLElement;
    if (target.parentNode === event.currentTarget) {
      event.preventDefault();
      event.stopPropagation();
      if (!!body) {
        setExpanded((prev) => !prev);
      } else if (onClick) onClick();
    }
  };

  const createMobileBody = (row: React.ReactNode) => {
    return React.Children.toArray(row).map((child: any) => {
      const cells = child.props.children;
      let elems = React.Children.map(cells, (cell, idx) => {
        if (!React.isValidElement(cell)) return null;
        let props = { isMobile: true, sx: { display: 'flex', alignItems: 'center' }, label: body?.options[idx]?.label ?? '' };
        return React.cloneElement(cell, props);
      });
      return elems;
    });
  };

  return isMobile || isInternalMobile ? (
    <Paper sx={{ background: 'white', marginBottom: 2, display: 'grid' }}>
      <Table sx={{ cursor: 'pointer' }}>
        <TableBody>
          <StyledTableRow onClick={handleOnClick}>
            {React.Children.map(children, (child, idx) => {
              if (React.isValidElement(child) && !!options) {
                let props = { isMobile: true, label: options[idx]?.label ?? '' };
                return React.cloneElement(child, props);
              }
            })}
            {!!body ? (
              <ListCell align="right">
                <Button variant="text" size="small" onClick={() => setExpanded((prev) => !prev)}>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    {intl.formatMessage({ id: 'general.moreInfo' })} {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </Typography>
                </Button>
              </ListCell>
            ) : null}
          </StyledTableRow>
          {!!body && (
            <StyledTableRow onClick={handleOnClick}>
              <Collapse in={expanded} timeout="auto">
                {createMobileBody(body.children)}
              </Collapse>
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  ) : (
    <>
      <StyledTableRow
        hover
        sx={sx}
        className={className}
        classes={{
          root: expanded ? classes.tableRowExpanded : classes.tableRow,
          hover: onClick ? classes.tableRowHover : classes.tableRowNoHover,
        }}
        onClick={handleOnClick}>
        {children}
        {!!body ? (
          <ListCell>
            <IconButton
              sx={{
                minWidth: '0px !important',
                padding: '0px !important',
              }}
              color="inherit"
              size="small"
              onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </IconButton>
          </ListCell>
        ) : null}
      </StyledTableRow>
      {!!body && (
        <BodyTableRow>
          <BodyCell
            classes={{
              root: expanded ? 'expanded' : 'default',
            }}
            colSpan={body.span}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <BodyTable sx={{ borderCollapse: 'collapse' }} size="small" aria-label="Body Table">
                <TableHead>
                  <BodyTableRow>
                    {body.options.map((headCell) => (
                      <TableCell
                        className={`${classes.tableHeadCell} ${headCell.sticky ? classes.sticky : ''} ${
                          headCell.autoWidth ? classes.autoWidth : ''
                        } ${headCell.fullWidth ? classes.fullWidth : ''}`}
                        align={headCell.align}
                        key={headCell.id}>
                        {headCell.label}
                      </TableCell>
                    ))}
                  </BodyTableRow>
                </TableHead>
                <TableBody>{body?.children ?? null}</TableBody>
              </BodyTable>
            </Collapse>
          </BodyCell>
        </BodyTableRow>
      )}
      <StyledTableRow sx={{ height: theme.spacing(2) }} />
    </>
  );
};

export default ListRow;
